import React from "react";
import { motion } from "framer-motion";
import { Crown, Sparkles, BadgeCheck, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { hasActiveSubscription } from "../../utils/subscription";
import { useTheme } from "../../theme/useTheme";
import { useAuthGate } from "../../utils/useAuthGate";

// Same palette the sidebar and the rest of Sanctuary use.
const COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
  gold: "#FFD479",
};

const PERKS = [
  "astriaHome.premium.perk1",
  "astriaHome.premium.perk2",
  "astriaHome.premium.perk3",
];

/** Formats the renewal date, or returns null when there isn't a usable one. */
const formatRenewal = (value?: string, locale?: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const PremiumCard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { requireAuth } = useAuthGate();
  const { user } = useAuth();
  const isLight = useTheme().theme === "light";
  // Gold reads on dark; on light it vanishes, so use the violet accent there.
  const gold = isLight ? "#7A5CFF" : COLORS.gold;
  const brandName = "Astria";

  // The card always renders; subscribers see their plan instead of an upsell.
  const isSubscribed = hasActiveSubscription(user);
  const renewsOn = isSubscribed
    ? formatRenewal((user as any)?.subscriptionEndDate, i18n.language)
    : null;

  return (
    <motion.section
      className="w-full mt-5 sm:mt-6"
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{ marginTop: "2.5rem" }}
    >
      <div
        className="relative overflow-hidden rounded-[28px] px-6 py-7 sm:px-9 sm:py-9 mx-auto"
        style={{
          maxWidth: 900,
          boxShadow: isLight
            ? "0 8px 24px rgba(26,26,31,0.08)"
            : "0 18px 40px rgba(0,0,0,0.45)",
          background: isLight ? "#FFFFFF" : "var(--astria-surface)",
          border: isLight ? "1px solid #E4E4EE" : "1px solid rgba(167,139,255,0.28)",
        }}
      >
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <span
                className="flex items-center justify-center w-11 h-11 rounded-2xl flex-shrink-0"
                style={{
                  backgroundColor: "var(--astria-accent-glow)",
                  border: "1px solid rgba(167,139,255,0.35)",
                }}
              >
                {isSubscribed ? (
                  <BadgeCheck
                    className="w-[19px] h-[19px]"
                    style={{ color: gold }}
                  />
                ) : (
                  <Crown
                    className="w-[19px] h-[19px]"
                    style={{ color: gold }}
                  />
                )}
              </span>
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{
                  backgroundColor: "var(--astria-accent-glow)",
                  color: "var(--astria-text-primary)",
                }}
              >
                <Sparkles className="w-3 h-3" style={{ color: gold }} />
                {isSubscribed
                  ? t("astriaHome.premium.badgeActive")
                  : t("astriaHome.premium.badge")}
              </span>
            </div>

            <h2 className="mt-4 text-2xl sm:text-3xl font-bold leading-tight text-astria-text">
              {isSubscribed
                ? t("astriaHome.premium.titleActive", { brand: brandName })
                : t("astriaHome.premium.titleUnlock", { brand: brandName })}
            </h2>

            <p className="mt-2.5 text-[13px] sm:text-[15px] leading-relaxed text-astria-muted max-w-[540px]">
              {isSubscribed
                ? renewsOn
                  ? t("astriaHome.premium.bodyRenews", { date: renewsOn })
                  : t("astriaHome.premium.bodyActive", { brand: brandName })
                : t("astriaHome.premium.bodyUnlock")}
            </p>

            <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
              {PERKS.map((perk) => (
                <li
                  key={perk}
                  className="flex items-center gap-1.5 text-[12px] sm:text-[13px] text-astria-text"
                >
                  {isSubscribed ? (
                    <Check
                      className="w-3 h-3 flex-shrink-0"
                      style={{ color: gold }}
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  ) : (
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: gold }}
                      aria-hidden="true"
                    />
                  )}
                  {t(perk)}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex-shrink-0 w-full lg:w-auto">
            <button
              type="button"
              onClick={requireAuth(() =>
                navigate("/account/plans-billing"),
              )}
              style={{
                backgroundColor: "var(--astria-accent-primary)",
                color: "var(--astria-bg-app)",
                boxShadow: isLight ? "0 6px 16px rgba(122,92,255,0.28)" : "var(--astria-shadow-button)",
              }}
              className="w-full lg:w-auto inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5
                text-[15px] font-bold transition-all duration-300
                hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(0,0,0,0.22)] active:scale-[0.98]
                focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a78bff] focus-visible:ring-offset-2
                focus-visible:ring-offset-[#0f0a14]"
            >
              {isSubscribed
                ? t("astriaHome.premium.ctaActive")
                : t("astriaHome.premium.ctaSignIn")}
            </button>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default PremiumCard;
